import { FormikErrors } from 'formik';

import { CommercialProjectDonationFormValues } from '@/app/components/DonationForm/CommercialProjectDonationForm/CommercialProjectDonationForm';
import { DonationFormBaseValues } from '@/app/components/DonationForm/types';
import { DonationFormType, PaymentMethod } from '@/app/enums';
import { getCurrency } from '@/app/utils/currency';
import { getPriceInCents, getTipInCents } from '@/app/utils/numbers';
import { getDateObjectFromISOString } from '@/app/utils/transforms';

import {
  SingleDonationFormValuesAuthenticatedUser,
  SingleDonationFormValuesUnauthenticatedUser,
} from '@/app/components/DonationForm/SingleDonationForm/SingleDonationForm';

import {
  ApiRequestValidationExceptionResponse,
  CreateDonationRequest,
  CreateDonationRequestPaymentMethodEnum,
} from '@/app/api';

import {
  LongTermDonationFormValuesAuthenticatedUser,
  LongTermDonationFormValuesUnauthenticatedUser,
} from '@/app/components/DonationForm/LongTermDonationForm/LongTermDonationForm';

import {
  GoodHabitDonationFormValuesAuthenticatedUser,
  GoodHabitDonationFormValuesUnauthenticatedUser,
} from '@/app/components/DonationForm/GoodHabitDonationForm/GoodHabitDonationForm';
import { getMaxAmountBasedTipOption } from '@/app/utils/countryConfig/amountBasedTipOptions';

const transformPaymentMethodToCreateDonationRequestPaymentMethod = (paymentMethodLocal: PaymentMethod) => {
  switch (paymentMethodLocal) {
    case PaymentMethod.WireTransfer:
    case PaymentMethod.QrCode:
      return CreateDonationRequestPaymentMethodEnum.WireTransfer;
    case PaymentMethod.Card:
      return CreateDonationRequestPaymentMethodEnum.Card;
    default:
      return null;
  }
};

type AnyDonationForm =
  | SingleDonationFormValuesUnauthenticatedUser
  | SingleDonationFormValuesAuthenticatedUser
  | LongTermDonationFormValuesUnauthenticatedUser
  | LongTermDonationFormValuesAuthenticatedUser
  | GoodHabitDonationFormValuesUnauthenticatedUser
  | GoodHabitDonationFormValuesAuthenticatedUser;

export const isSingleDonationFormValuesUnauthenticated = (
  formValues: SingleDonationFormValuesUnauthenticatedUser | SingleDonationFormValuesAuthenticatedUser,
): formValues is SingleDonationFormValuesUnauthenticatedUser =>
  formValues.formType === DonationFormType.SINGLE_UNAUTHENTICATED_USER;

export const isSingleDonationFormValuesAuthenticated = (
  formValues: AnyDonationForm,
): formValues is SingleDonationFormValuesUnauthenticatedUser =>
  formValues.formType === DonationFormType.SINGLE_AUTHENTICATED_USER;

export const isLongTermDonationFormValuesAuthenticated = (
  formValues: AnyDonationForm,
): formValues is LongTermDonationFormValuesAuthenticatedUser =>
  formValues.formType === DonationFormType.LONG_TERM_AUTHENTICATED_USER;

export const isLongTermDonationFormValuesUnauthenticated = (
  formValues: AnyDonationForm,
): formValues is LongTermDonationFormValuesUnauthenticatedUser =>
  formValues.formType === DonationFormType.LONG_TERM_UNAUTHENTICATED_USER;

export const isGoodHabitFormValuesUnauthenticated = (
  formValues: AnyDonationForm,
): formValues is GoodHabitDonationFormValuesUnauthenticatedUser =>
  formValues.formType === DonationFormType.GOOD_HABIT_UNAUTHENTICATED_USER;

export const isGoodHabitFormValuesAuthenticated = (
  formValues: AnyDonationForm,
): formValues is GoodHabitDonationFormValuesAuthenticatedUser =>
  formValues.formType === DonationFormType.GOOD_HABIT_AUTHENTICATED_USER;

const getTip = (formValues: DonationFormBaseValues, enableAmountBasedTips: boolean) => {
  const shouldUseCustomTipAmount = !!formValues.customTipAmount || formValues.customTipAmount === 0;

  if (formValues.amount <= getMaxAmountBasedTipOption() && enableAmountBasedTips) {
    return getPriceInCents(formValues.tipAmount || formValues.customTipAmount || 0);
  }
  return shouldUseCustomTipAmount
    ? getPriceInCents(formValues.customTipAmount || 0)
    : getTipInCents(formValues.amount || 0, formValues.tipPercentage || 0);
};

type TransformDonationFormBaseValuesToCreateDonationRequestParams = {
  abGroup: string;
  projectId: string;
  formValues: DonationFormBaseValues;
  subProject?: string;
  enableAmountBasedTips?: boolean;
};

const transformDonationFormBaseValuesToCreateDonationRequest = ({
  abGroup,
  projectId,
  formValues,
  subProject,
  enableAmountBasedTips = true,
}: TransformDonationFormBaseValuesToCreateDonationRequestParams): CreateDonationRequest => {
  const amountInCents = getPriceInCents(formValues.amount || 0);
  const paymentMethod = transformPaymentMethodToCreateDonationRequestPaymentMethod(formValues.paymentMethod);

  const tipInCents = getTip(formValues, enableAmountBasedTips);

  if (!paymentMethod) {
    throw new Error('Missing payment method');
  }

  if (typeof amountInCents !== 'number') {
    throw new Error('Invalid amount given');
  }

  if (typeof tipInCents !== 'number') {
    throw new Error('Invalid tip given');
  }

  const birthDate = formValues.birthDate ? getDateObjectFromISOString(formValues.birthDate) : undefined;

  return {
    project: projectId,
    mode: formValues.donationMode,
    amountInCents,
    tipInCents,
    currency: getCurrency(),
    paymentMethod,
    firstName: formValues.firstName || undefined,
    lastName: formValues.lastName || undefined,
    fullName: formValues.nameOnReceipt || undefined,
    street: formValues.streetAndHouseNumber || undefined,
    city: formValues.city || undefined,
    zip: formValues.zipCode || undefined,
    country: formValues.country || undefined,
    companyLegalId: formValues.tin || undefined,
    birthdate: birthDate,
    donationReceipt: formValues.donationReceipt,
    isGiftCertificateRequested: formValues.certificateOfGoodness,
    giftCertificateRecipientName: formValues.certificateRecipient || undefined,
    giftCertificateDonorName: formValues.certificateDonor || undefined,
    newsletterOptOut: formValues.noNewsletter || false,
    agreedToShareContact: !!formValues.agreedToShareContact,
    subproject: subProject,
    phoneNumber: formValues.phoneNumber || undefined,
  };
};

export const transformSingleDonationFormValuesToCreateDonationRequest = (
  projectId: string,
  formValues: SingleDonationFormValuesAuthenticatedUser | SingleDonationFormValuesUnauthenticatedUser,
  userId: string | undefined,
  subProject: string | undefined,
  abGroup: string,
): CreateDonationRequest => {
  const base: CreateDonationRequest = transformDonationFormBaseValuesToCreateDonationRequest({
    abGroup,
    projectId,
    formValues,
    subProject,
    enableAmountBasedTips: true,
  });

  if (isSingleDonationFormValuesUnauthenticated(formValues)) {
    return {
      ...base,
      email: formValues.email || undefined,
    };
  }

  if (isSingleDonationFormValuesAuthenticated(formValues)) {
    if (!userId) {
      throw new Error('User ID not provided');
    }

    return {
      ...base,
      user: userId,
    };
  }

  // no-op
  throw new Error('Invalid form type');
};

export const transformGoodHabitDonationFormValuesToCreateDonationRequest = (
  projectId: string,
  formValues: GoodHabitDonationFormValuesAuthenticatedUser | GoodHabitDonationFormValuesUnauthenticatedUser,
  abGroup: string,
  userId?: string,
): CreateDonationRequest => {
  const base: CreateDonationRequest = {
    ...transformDonationFormBaseValuesToCreateDonationRequest({
      abGroup,
      projectId,
      formValues,
      enableAmountBasedTips: false,
    }),
    goodHabitCategory: formValues.categoryId,
  };

  if (isGoodHabitFormValuesUnauthenticated(formValues)) {
    return {
      ...base,
      email: formValues.email || undefined,
    };
  }

  if (isGoodHabitFormValuesAuthenticated(formValues)) {
    if (!userId) {
      throw new Error('User ID not provided');
    }

    return {
      ...base,
      user: userId,
    };
  }

  // no-op
  throw new Error('Invalid form type');
};

export const transformLongTermDonationFormValuesToCreateDonationRequest = (
  projectId: string,
  formValues: LongTermDonationFormValuesAuthenticatedUser | LongTermDonationFormValuesUnauthenticatedUser,
  userId: string | undefined,
  subProject: string | undefined,
  abGroup: string,
): CreateDonationRequest => {
  const base: CreateDonationRequest = transformDonationFormBaseValuesToCreateDonationRequest({
    abGroup,
    projectId,
    formValues,
    subProject,
    enableAmountBasedTips: true,
  });

  if (isLongTermDonationFormValuesUnauthenticated(formValues)) {
    return {
      ...base,
      email: formValues.email || undefined,
    };
  }

  if (isLongTermDonationFormValuesAuthenticated(formValues)) {
    if (!userId) {
      throw new Error('User ID not provided');
    }

    return {
      ...base,
      user: userId,
    };
  }

  // no-op
  throw new Error('Invalid form type');
};

export const transformCreateDonationViolationsToDonationFormErrors = (
  errors: ApiRequestValidationExceptionResponse['violations'],
): FormikErrors<
  | SingleDonationFormValuesAuthenticatedUser
  | SingleDonationFormValuesUnauthenticatedUser
  | GoodHabitDonationFormValuesAuthenticatedUser
  | GoodHabitDonationFormValuesUnauthenticatedUser
  | LongTermDonationFormValuesAuthenticatedUser
  | LongTermDonationFormValuesUnauthenticatedUser
> => {
  // Following params are not mapped to form fields:
  // project, subproject, reward, rewardVariant, note, currency, user, phoneNumber
  const apiViolationKeysToFormFieldKeysMap: Record<
    string,
    | keyof SingleDonationFormValuesAuthenticatedUser
    | keyof SingleDonationFormValuesUnauthenticatedUser
    | keyof GoodHabitDonationFormValuesAuthenticatedUser
    | keyof GoodHabitDonationFormValuesUnauthenticatedUser
    | keyof LongTermDonationFormValuesAuthenticatedUser
    | keyof LongTermDonationFormValuesUnauthenticatedUser
  > = {
    firstName: 'firstName',
    lastName: 'lastName',
    email: 'email',
    city: 'city',
    country: 'country',
    mode: 'donationMode',
    amountInCents: 'amount',
    tipInCents: 'tipPercentage',
    paymentMethod: 'paymentMethod',
    fullName: 'nameOnReceipt',
    street: 'streetAndHouseNumber',
    zip: 'zipCode',
    companyLegalId: 'tin',
    birthdate: 'birthDate',
    giftCertificateRecipientName: 'certificateRecipient',
    giftCertificateDonorName: 'certificateDonor',
    goodHabitCategory: 'categoryId',
    donationReceipt: 'donationReceipt',
    isGiftCertificateRequested: 'certificateOfGoodness',
    newsletterOptOut: 'noNewsletter',
    agreedToShareContact: 'agreedToShareContact',
  };

  if (!errors) {
    return {};
  }

  return Object.entries(errors).reduce((acc, [key, value]) => {
    if (Object.keys(apiViolationKeysToFormFieldKeysMap).includes(key) && value.length) {
      return {
        ...acc,
        [apiViolationKeysToFormFieldKeysMap[key]]: value[0],
      };
    }

    return acc;
  }, {});
};

export const transformCommercialProjectDonationFormValuesToCreateDonationRequest = (
  abGroup: string,
  projectId: string,
  formValues: CommercialProjectDonationFormValues,
  userId: string | undefined,
  subProject: string | undefined,
  rewardId: string,
  rewardVariantId?: string,
): CreateDonationRequest => {
  const base: CreateDonationRequest = transformDonationFormBaseValuesToCreateDonationRequest({
    abGroup,
    projectId,
    formValues,
    subProject,
    enableAmountBasedTips: true,
  });

  const commercialProjectBase = {
    ...base,
    reward: rewardId,
    rewardVariant: rewardVariantId,
    phoneNumber: formValues.phoneNumber || undefined,
    note: formValues.note || undefined,
  };

  if (userId) {
    return {
      ...commercialProjectBase,
      user: userId,
    };
  }

  return {
    ...commercialProjectBase,
    email: formValues.email || undefined,
  };
};
